/**
 * HistoryPanel
 *
 * 直近10件の解析結果を一覧表示するモーダルパネル。
 *
 * - 各エントリ：時刻、モード、結果テキスト
 * - 「全て削除」ボタンと「閉じる」ボタン
 * - 履歴が空の場合はプレースホルダ表示
 */

import type { Mode, ScanResult } from '../types';

import styles from './HistoryPanel.module.css';

export interface HistoryPanelProps {
  history: ScanResult[];
  onClear: () => void;
  onClose: () => void;
}

const MODE_LABEL: Record<Mode, string> = {
  translate: 'TRANSLATION',
  summarize: 'SUMMARY',
  interpret: 'INTERPRET',
};

/**
 * ISO文字列を「MM/DD HH:MM」形式に整形
 * 履歴は時刻だけだとどの日かわからないので日付も入れる
 */
function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${min}`;
}

export function HistoryPanel({ history, onClear, onClose }: HistoryPanelProps) {
  const hasItems = history.length > 0;

  const handleClear = () => {
    if (window.confirm('履歴をすべて削除しますか？')) {
      onClear();
    }
  };

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="history-title">
      <div className={styles.dialog}>
        <div className={styles.header}>
          <h2 id="history-title" className={styles.title}>HISTORY</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="履歴パネルを閉じる"
          >
            ×
          </button>
        </div>

        {hasItems ? (
          <ul className={styles.list}>
            {history.map((entry) => (
              <li key={entry.id} className={styles.entry}>
                <div className={styles.entryMeta}>
                  <span>{MODE_LABEL[entry.mode]}</span>
                  <span className={styles.entryTimestamp}>{formatTimestamp(entry.timestamp)}</span>
                </div>
                <p className={styles.entryText}>{entry.text}</p>
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.empty}>
            <span>NO HISTORY</span>
            <span>解析結果がここに保存されます（最大10件）</span>
          </div>
        )}

        <div className={styles.footer}>
          {hasItems && (
            <button
              type="button"
              className={`${styles.button} ${styles.buttonDanger}`}
              onClick={handleClear}
            >
              CLEAR ALL
            </button>
          )}
          <button type="button" className={styles.button} onClick={onClose}>
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}
